import React from "react";
import { AppLink } from "../../../components/ui/link";
import type { MainAppPageProps } from "../types";

type PrivacyPageProps = Pick<MainAppPageProps, "routes">;

const SECTION_CLASS = "mb-8 last:mb-0";
const HEADING_CLASS = "mb-3 font-display text-lg font-semibold text-foreground";
const PARA_CLASS = "mb-3 text-sm leading-relaxed text-muted-foreground";

export const PrivacyPage: React.FC<PrivacyPageProps> = ({ routes }) => (
  <article className="mx-auto max-w-3xl">
    <h1 className="mb-8 font-display text-2xl font-semibold tracking-tight text-foreground">
      Privacy Policy
    </h1>
    <p className="mb-8 text-sm leading-relaxed text-muted-foreground">
      Last updated: October 31, 2026
    </p>

    <section className={SECTION_CLASS}>
      <h2 className={HEADING_CLASS}>1. Information We Collect</h2>
      <p className={PARA_CLASS}>
        We collect information you provide: account credentials (email,
        password), Organization details (name, phone), membership roles, and
        invitation emails when you invite teammates. We collect website business
        information (name, services, contact details), quote and booking data
        from your website visitors, and payment information processed by our
        payment providers, including Stripe for web and Android billing and
        Apple for iPhone or iPad App Store subscriptions. If you use the
        job-site photos product or the Toolkit mobile app, we also collect
        job-site photos and videos you upload, optional tags and annotations,
        device location while the app is in use only if you allow it (to
        auto-match photos to a nearby job site; the app still works if location
        is off), and push notification tokens. If you use the credentials
        product, we collect photos of licenses and certificates you upload, the
        details we read from them (name, issuer, numbers, dates, category),
        reminder preferences, and share-link tokens you create. Public share
        links can be viewed by anyone who has the URL until you revoke them or
        they expire. Messages you exchange with our in-product AI assistant are
        stored so we can operate the service, troubleshoot, and meet legal and
        security obligations. The assistant is used to configure and edit
        products after they exist; it does not create your Organization. We also
        collect first-party aggregated traffic metrics for each contractor
        subdomain (approximate counts of distinct browsers per UTC day,
        excluding dashboard Live Preview loads so owner testing is not mixed
        with public visits) to operate the service, share optional traffic
        recaps during trial, and demonstrate value. We also collect usage data,
        logs, and device information to operate and improve the service.
      </p>
    </section>

    <section className={SECTION_CLASS}>
      <h2 className={HEADING_CLASS}>2. How We Use Information</h2>
      <p className={PARA_CLASS}>
        We use your information to provide, maintain, and improve ToolKit
        products (website, job-site photos, and credentials); to operate your
        Organization and memberships; to send invitations you request; to
        process transactions; to send service-related communications; and to
        comply with legal obligations. We may use aggregated, anonymized data
        for analytics and product development.
      </p>
    </section>

    <section className={SECTION_CLASS}>
      <h2 className={HEADING_CLASS}>3. Sharing of Information</h2>
      <p className={PARA_CLASS}>
        We share information with service providers who assist in operating
        ToolKit (e.g., hosting, payment processing via Stripe and Apple,
        analytics). We share Organization data with members of that Organization
        as needed to operate the products (for example, website profile for
        owners and admins, job-site photos with teammates who have access, and
        organization-visible credentials while a person is on the team).
        Invitation emails are sent to the addresses you provide. We do not sell
        your personal information. We may disclose information if required by
        law or to protect our rights and safety.
      </p>
    </section>

    <section className={SECTION_CLASS}>
      <h2 className={HEADING_CLASS}>
        4. AI Processing of Your Business Information
      </h2>
      <p className={PARA_CLASS}>
        ToolKit uses third-party large language model (&ldquo;LLM&rdquo;)
        providers — currently OpenAI — to power features such as the in-product
        AI assistant and the automatic generation of SEO content for your
        contractor website (per-service pages, per-area pages, FAQ pages, blog
        drafts, and related copy). The assistant helps you configure and edit
        products after they exist; it does not create your Organization. To do
        this, we send relevant business information you provided (such as
        services, service areas, FAQs, business name, and public contact
        details) and your messages with the in-product assistant to the LLM
        provider. When you upload a credential photo, we send that image to the
        LLM provider only to extract printed text such as names, issuers, and
        dates. We do not send account passwords, payment details, or your
        customers&rsquo; quote/booking submissions to LLM providers.
        LLM-generated content is then stored in our database and served from
        your contractor site; we cache the generated copy and only re-run the
        model when your underlying business inputs change, which both reduces
        processing of your data and limits unnecessary sharing with the LLM
        provider.
      </p>
      <p className={PARA_CLASS}>
        LLM providers process this data on our behalf as service providers under
        contractual terms that, among other things, prohibit them from using
        your data to train their generally-available models. You can review the
        current LLM provider&rsquo;s privacy and data-handling practices on
        their public documentation. If you do not want your business information
        processed by an LLM provider for SEO content generation, contact us — we
        can disable AI-generated SEO pages for your account and serve only the
        truthful template-based fallback.
      </p>
    </section>

    <section className={SECTION_CLASS}>
      <h2 className={HEADING_CLASS}>5. Cookies and Tracking</h2>
      <p className={PARA_CLASS}>
        We use cookies and similar technologies for authentication, preferences,
        public contractor-site traffic estimation (anonymous browser identifiers
        on your subdomain alongside server-side aggregates), marketing site
        analytics (such as Google Analytics where enabled), and product
        measurement. You can adjust browser settings to limit cookies, but some
        features may not function properly.
      </p>
    </section>

    <section className={SECTION_CLASS}>
      <h2 className={HEADING_CLASS}>6. Data Retention</h2>
      <p className={PARA_CLASS}>
        We retain your data for as long as your Organization or account is
        active or as needed to provide the service. You can delete your user
        account in Settings (web) or Profile (Toolkit app). That removes your
        login and personal credentials. Job sites, photos, and other company
        content you added stay with the Organization. After account termination,
        we may retain organization records as required by law or for legitimate
        business purposes.
      </p>
    </section>

    <section className={SECTION_CLASS}>
      <h2 className={HEADING_CLASS}>7. Your Rights</h2>
      <p className={PARA_CLASS}>
        Depending on your location, you may have rights to access, correct,
        delete, or port your personal data, or to object to or restrict
        processing. You can delete your ToolKit user account in the app as
        described above. Contact us to exercise other rights. You may also have
        the right to lodge a complaint with a supervisory authority.
      </p>
    </section>

    <section className={SECTION_CLASS}>
      <h2 className={HEADING_CLASS}>8. Security</h2>
      <p className={PARA_CLASS}>
        We implement reasonable security measures to protect your data. However,
        no method of transmission over the Internet or electronic storage is
        completely secure. We cannot guarantee absolute security.
      </p>
    </section>

    <section className={SECTION_CLASS}>
      <h2 className={HEADING_CLASS}>9. Children</h2>
      <p className={PARA_CLASS}>
        ToolKit is not intended for children under 16. We do not knowingly
        collect information from children.
      </p>
    </section>

    <section className={SECTION_CLASS}>
      <h2 className={HEADING_CLASS}>10. Changes</h2>
      <p className={PARA_CLASS}>
        We may update this policy from time to time. Material changes will be
        posted here with an updated date. Continued use after changes
        constitutes acceptance.
      </p>
    </section>

    <p className="mt-8 text-sm text-muted-foreground">
      Questions? See our{" "}
      <AppLink to={routes.faq} variant="premium">
        FAQ
      </AppLink>{" "}
      or{" "}
      <AppLink to={routes.terms} variant="premium">
        Terms of Service
      </AppLink>
      .
    </p>
  </article>
);
